import dayjs from "dayjs";
import { SmallTriangleWarning } from "components/icons/SmallTriangleWarning";
import { ReactNode } from "react";
import { ClientProgressCycle, TaskStepDetails } from "types/Models/task";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ConditionalLink } from "components/conditionalLink";
import { DISPLAY_DATE } from "constants/date";
import { ACTIVITY_TAB, TAB_PARAM } from "constants/chatTab";
import { getIsEtdHidden } from "utils/getIsEtdHidden";
import { STAGE_KEY_MISSING_INFORMATION } from "constants/clientProgressStageKeys";
import { JourneyCycleHeader } from "./JourneyCycleHeader";
import { JourneyPreviousCycle } from "./JourneyPreviousCycle";
import { JourneySteps } from "./JourneySteps";

export { STAGE_ICONS } from "./JourneySteps";

type JourneyContentProps = {
  stepDetails: TaskStepDetails[];
  latestEtd?: string | null;
  taskUuid?: string | null;
  previousCycles?: ClientProgressCycle[];
};

const NO_PREVIOUS_CYCLES: ClientProgressCycle[] = [];

const JourneyContent = ({
  stepDetails,
  latestEtd,
  taskUuid,
  previousCycles = NO_PREVIOUS_CYCLES,
}: JourneyContentProps) => {
  const hasPreviousCycles = previousCycles.length > 0;
  const currentCycleLabel = `Amendment ${previousCycles.length}`;

  const isMissingInformation = stepDetails?.some(
    (step) => step.isCurrent && step.key === STAGE_KEY_MISSING_INFORMATION
  );
  const isEtdHidden = stepDetails?.some(
    (step) => step.isCurrent && getIsEtdHidden(step.key)
  );

  return (
    <section className="t-rounded-s t-min-w-56 t-max-w-[264px]">
      <div className="t-max-h-80 t-overflow-y-auto hide-scrollbar">
        {previousCycles.map((cycle) => (
          <JourneyPreviousCycle key={cycle.amendment_number} cycle={cycle} />
        ))}
        {hasPreviousCycles && <JourneyCycleHeader label={currentCycleLabel} />}
        <JourneySteps stepDetails={stepDetails} />
      </div>
      {(isMissingInformation || (latestEtd && !isEtdHidden) || taskUuid) && (
        <>
          <div className="t-my-3 t-border-t t-border-dashed t-border-b-0 t-border-neutral-70" />
          {isMissingInformation ? (
            <div className="t-flex t-items-start t-gap-1.5 t-text-left">
              <span className="t-flex-shrink-0 t-text-orange">
                <SmallTriangleWarning className="t-size-3.5" />
              </span>
              <span className="t-text-body-sm t-italic t-text-neutral-20">
                Estimated draft date available after pending details are
                submitted.
              </span>
            </div>
          ) : (
            latestEtd &&
            !isEtdHidden && (
              <div className="t-text-body-sm t-text-text-30 t-text-left">
                <span className="t-italic t-text-neutral-20">
                  Draft expected by:{" "}
                </span>
                <span className="t-font-normal t-text-white">
                  {dayjs(latestEtd).format(DISPLAY_DATE)}
                </span>
              </div>
            )
          )}
          {taskUuid && (
            <ConditionalLink
              to={`/tax/filings/${taskUuid}?${TAB_PARAM}=${ACTIVITY_TAB}`}
              className="t-text-body-sm t-mt-1 t-block t-text-left t-text-purple-40"
            >
              View all updates &rsaquo;
            </ConditionalLink>
          )}
        </>
      )}
    </section>
  );
};

export const JourneyTooltip = ({
  children,
  stepDetails,
  latestEtd,
  taskUuid,
  previousCycles,
}: JourneyContentProps & { children: ReactNode }) => {
  return (
    <ConditionalToolTip
      propClass="!t-p-4"
      collisionPadding={16}
      condition={
        stepDetails?.length > 0 && (
          <JourneyContent
            stepDetails={stepDetails}
            latestEtd={latestEtd}
            taskUuid={taskUuid}
            previousCycles={previousCycles}
          />
        )
      }
    >
      {children}
    </ConditionalToolTip>
  );
};
